import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import some from "lodash/some";

import { useCurrentClient } from "../../context/Client";

import { QUERY_CURRENT_USER } from "../../config/graphql/query";

export const useUserHasRole = (user: IUser | undefined, roles: UserRole[]) => {
  const hasRole = useMemo(
    () => some(roles, (role) => user?.roles.includes(role)),
    [roles, user],
  );

  return hasRole;
};

export const useHasRole = (roles: UserRole[]) => {
  const { data } = useQuery<{ me: IUser }>(QUERY_CURRENT_USER);

  const hasRole = useUserHasRole(data?.me, roles);

  return hasRole;
};

export const useIsCurrentUserReseller = () => {
  const { data } = useQuery<{ me: IUser }>(QUERY_CURRENT_USER);

  const clientType = data?.me?.client.type;

  return data?.me?.roles?.includes("CLIENT_ADMIN") && clientType === "reseller";
};

export const useIsCurrentUserDistributor = () => {
  const { data } = useQuery<{ me: IUser }>(QUERY_CURRENT_USER);

  const clientType = data?.me?.client.type;

  return (
    data?.me?.roles?.includes("CLIENT_ADMIN") && clientType === "distributor"
  );
};

/**
 * True when a distributor/reseller Client Admin is currently acting on a client
 * below them in the tree, rather than on their own.
 *
 * Pages holding end-customer personal data (employees, visitors, meetings,
 * activity, students, courier deliveries, access labels) are hidden in that
 * case. Inside their own client they keep everything — otherwise that client
 * would have no admin at all.
 *
 * ADMIN is never affected
 *
 * The backend enforces the same rule via `@auth(excludeClientType: ...)`; this
 * hook only keeps the UI from offering what the API would refuse.
 */
export const useIsForeignClientContext = () => {
  const { data } = useQuery<{ me: IUser }>(QUERY_CURRENT_USER);

  const currentClient = useCurrentClient();

  const me = data?.me;
  const userClient = me?.client;

  return useMemo(() => {
    if (!me?.roles?.includes("CLIENT_ADMIN")) {
      return false;
    }

    if (me.roles.includes("ADMIN")) {
      return false;
    }

    if (userClient?.type !== "distributor" && userClient?.type !== "reseller") {
      return false;
    }

    if (!currentClient?.id || !userClient?.id) {
      return false;
    }

    return currentClient.id !== userClient.id;
  }, [me, userClient, currentClient]);
};

export const useCurrentUserClient = () => {
  const { data } = useQuery<{ me: IUser }>(QUERY_CURRENT_USER);

  return data?.me?.client;
};

export default useHasRole;
